import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Mail } from "lucide-react";
import Logo from "@/components/Logo";
import QuoteDialog from "@/components/QuoteDialog";
import { EMAIL } from "@/lib/data";
import { EASE } from "@/components/Reveal";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [quoteOpen, setQuoteOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setMenuOpen(false), [pathname]);

    return (
        <>
            <header
                className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                    scrolled
                        ? "border-b border-white/10 bg-[#0B0F17]/85 backdrop-blur-xl"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <div className="container-x flex h-[72px] items-center justify-between gap-6">
                    <Logo />

                    <nav className="hidden items-center gap-8 md:flex">
                        {NAV.map((item) => {
                            const active =
                                item.to === "/"
                                    ? pathname === "/"
                                    : pathname.startsWith(item.to);
                            return (
                                <Link
                                    key={item.to}
                                    to={item.to}
                                    className={`group relative font-display text-sm font-medium transition-colors duration-200 ${
                                        active
                                            ? "text-amber-400"
                                            : "text-slate-300 hover:text-white"
                                    }`}
                                >
                                    {item.label}
                                    <span
                                        className={`absolute -bottom-1.5 left-0 h-px bg-amber-400 transition-all duration-300 ${
                                            active
                                                ? "w-full"
                                                : "w-0 group-hover:w-full"
                                        }`}
                                    />
                                </Link>
                            );
                        })}
                    </nav>

                    <div className="hidden items-center gap-5 lg:flex">
                        <a
                            data-testid="nav-email-direct-link"
                            href={`mailto:${EMAIL}`}
                            className="flex items-center gap-2 font-mono text-xs text-slate-400 transition-colors hover:text-amber-400"
                        >
                            <Mail size={13} />
                            {EMAIL}
                        </a>
                        <button
                            data-testid="nav-request-quote-button"
                            onClick={() => setQuoteOpen(true)}
                            className="rounded-full bg-amber-500 px-5 py-2.5 font-display text-sm font-semibold text-[#0B0F17] transition-all duration-300 hover:-translate-y-0.5 hover:bg-amber-400"
                        >
                            Request a quote
                        </button>
                    </div>

                    <button
                        data-testid="nav-mobile-menu-button"
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white md:hidden"
                        onClick={() => setMenuOpen(true)}
                        aria-label="Open menu"
                    >
                        <Menu size={18} />
                    </button>
                </div>
            </header>

            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[70] flex flex-col bg-[#0B0F17]/98 backdrop-blur-xl md:hidden"
                    >
                        <div className="container-x flex h-[72px] items-center justify-between">
                            <Logo />
                            <button
                                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white"
                                onClick={() => setMenuOpen(false)}
                                aria-label="Close menu"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <nav className="container-x flex flex-1 flex-col justify-center gap-2">
                            {NAV.map((item, i) => (
                                <motion.div
                                    key={item.to}
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 0.6,
                                        delay: 0.08 + i * 0.07,
                                        ease: EASE,
                                    }}
                                >
                                    <Link
                                        to={item.to}
                                        className="block border-b border-white/10 py-4 font-display text-4xl font-bold tracking-tight text-white"
                                    >
                                        {item.label}
                                    </Link>
                                </motion.div>
                            ))}
                        </nav>
                        <div className="container-x pb-10">
                            <button
                                onClick={() => {
                                    setMenuOpen(false);
                                    setQuoteOpen(true);
                                }}
                                className="btn-primary w-full"
                            >
                                Request a quote
                            </button>
                            <a
                                href={`mailto:${EMAIL}`}
                                className="mt-5 flex items-center justify-center gap-2 font-mono text-xs text-slate-400"
                            >
                                <Mail size={13} />
                                {EMAIL}
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <QuoteDialog open={quoteOpen} onClose={() => setQuoteOpen(false)} />
        </>
    );
}
